import { IterableMap } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import type { TrackingPageEvent } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import classNames from "classnames";
import { memo, useMemo } from "react";
import { Flex } from "../../../flex";
import { replaceWordsByMerchantRequest } from "../../../settings-elements/order/order-details";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import { getBodyStyles, getLabelStyles } from "../shared/block-styles";
import * as trackingCss from "./tracking-details.module.css";

export const TrackingDetailLines = memo(function TrackingDetailLines({
  trackingEvents,
}: {
  trackingEvents: TrackingPageEvent[];
}) {
  const minHeight =
    Math.min(3, trackingEvents.length) * 34 +
    (Math.min(3, trackingEvents.length) - 1) * 8;

  if (window.innerWidth > 1000) {
    return (
      <div
        style={{ flex: 1, position: "relative", minHeight: `${minHeight}px` }}
      >
        <Flex
          bottom="0"
          dir="column"
          gap="md"
          left="0"
          overflowY="auto"
          position="absolute"
          right="0"
          top="0"
        >
          <IterableMap items={trackingEvents} keyFn={(_, i) => i}>
            {(event, index) => (
              <TrackingEventComponent
                event={event}
                filled={index === trackingEvents.length - 1}
                key={index}
              />
            )}
          </IterableMap>
        </Flex>
      </div>
    );
  }

  return null;
});

const TrackingEventComponent = memo(function TrackingEventComponent({
  event,
  filled,
}: {
  event: TrackingPageEvent;
  filled: boolean;
}) {
  const { returnAppSettings, pageSettings } =
    useRequiredContext(TrackingPageContext);
  const bodyStyles = getBodyStyles(pageSettings);
  const labelStyles = getLabelStyles(pageSettings);

  const date = useMemo(() => {
    try {
      if (!event.timestamp) return null;
      const instant = event.timestamp.toTemporalInstant();
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return instant.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: clientTimeZone,
      });
    } catch (e) {
      console.error("Error parsing date", e);
      return "Unknown";
    }
  }, [event.timestamp]);

  const message = useMemo(() => {
    const loc = event.location;
    const locationParts: string[] = [];

    if (loc && typeof loc !== "string") {
      if (loc.city) locationParts.push(loc.city);
      if (loc.state) locationParts.push(loc.state);
      if (loc.country) locationParts.push(loc.country);
    } else if (typeof loc === "string" && loc) {
      locationParts.push(loc);
    }

    const location =
      locationParts.length > 0 ? `, ${locationParts.join(", ")}` : "";

    return `${event.message}${location}`;
  }, [event]);

  return (
    <Flex
      className={trackingCss.historyEntry}
      dir="column"
      gap="xxs"
      justify="center"
      pl="3xl"
      position="relative"
      style={
        { "--history-entry-color": bodyStyles.color } as React.CSSProperties
      }
    >
      {filled && (
        <span
          className={trackingCss.filledCircle}
          style={{ backgroundColor: bodyStyles.color }}
        />
      )}
      {date && (
        <Text
          className={trackingCss.tinyText}
          fontWeight="semibold"
          style={bodyStyles}
        >
          {date}
        </Text>
      )}
      <span
        className={classNames(
          trackingCss.xTinyText,
          trackingCss.thin,
          trackingCss.grey,
        )}
        style={labelStyles}
      >
        {replaceWordsByMerchantRequest(message, returnAppSettings.storeUrl)}
      </span>
    </Flex>
  );
});
